import * as React from 'react';
import { Button, Grid, lighten, darken, SxProps, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/sharp-solid-svg-icons';
import { faCircle } from '@fortawesome/sharp-regular-svg-icons';
import { useL2DTheme } from '@europrocurement/flexy-components/hooks';

export type BooleanBadgeProps = {
    label: string;
    value?: boolean;
    icon?: boolean;
    logo?: string;
    sx?: SxProps;
    toggleAction?: () => void;
    disabled?: boolean;
};

export const BooleanBadge: React.FunctionComponent<BooleanBadgeProps> = function ({
    label,
    value = true,
    icon = false,
    logo = undefined,
    sx = {},
    toggleAction = undefined,
    disabled = false,
}: BooleanBadgeProps) {
    const { palette } = useL2DTheme();

    const deductBgColor = (): string => {
        let deductedBgColor = value ? palette.primary.main : palette.grey[300];

        if (!icon) {
            deductedBgColor = value ? palette.success.main : palette.error.main;
        }

        return palette.mode === 'light'
            ? lighten(deductedBgColor, 0.85)
            : darken(deductedBgColor, 0.6);
    };

    const deductColor = (): string => {
        let deductedColor = value ? palette.primary.main : palette.grey[600];

        if (!icon) {
            deductedColor = value ? palette.success.main : palette.error.main;
        }

        return palette.mode === 'light' ? deductedColor : lighten(deductedColor, 0.5);
    };

    const buttonStyle = {
        ...sx,
        display: 'flex',
        padding: icon ? '3px 3px 3px 6px' : '6px 10px',
        borderRadius: '50px',
        alignItems: 'center',
        gap: '5px',
        width: 'fit-content',
        color: deductColor(),
    };

    return (
        <Button
            onClick={toggleAction}
            sx={buttonStyle}
            disabled={disabled}
        >
            <Grid
                component="div"
                bgcolor={deductBgColor()}
                sx={buttonStyle}
                color={deductColor()}
            >
                {logo && icon && (
                    <img
                        src={logo}
                        alt="Logo du booleanBadge"
                        height={9}
                    />
                )}

                <Typography
                    variant="body2"
                    component="span"
                    fontWeight="bold"
                >
                    {label}
                </Typography>

                {icon &&
                    (value ? (
                        <FontAwesomeIcon icon={faCircleCheck} />
                    ) : (
                        <FontAwesomeIcon icon={faCircle} />
                    ))}
            </Grid>
        </Button>
    );
};
